import React, { useState, useEffect } from 'react';
import { useWebSocket } from '../../context/WebSocketContext';
import { Zap, X, CheckCircle, AlertTriangle, Wifi } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const NewChargerNotification = () => {
  const { subscribe, lastEvent } = useWebSocket();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Subscribe to charger events
    const unsubscribe = subscribe('charger', (data) => {
      // Check if this is a new_connection event
      if (data && (data.event_type === 'new_connection' || data.is_new_connection)) {
        handleNewCharger(data);
      }
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [subscribe]);

  // Also listen to lastEvent for charger.new_connection
  useEffect(() => {
    if (lastEvent?.type === 'charger.new_connection' && lastEvent?.data) {
      handleNewCharger(lastEvent.data);
    }
  }, [lastEvent]);

  const handleNewCharger = (data) => {
    console.log('New charger connection:', data);
    
    // Prevent duplicates
    const existingIds = notifications.map(n => n.charger_id);
    if (existingIds.includes(data.charger_id)) return;
    
    // Add to notifications list
    const notification = {
      id: Date.now(),
      ...data,
      timestamp: new Date().toISOString()
    };
    
    setNotifications(prev => [notification, ...prev].slice(0, 5)); // Keep max 5
    
    // Also show toast
    if (data.is_registered) {
      toast.success(
        <div className="flex items-start gap-3">
          <Wifi className="h-5 w-5 text-green-500 mt-0.5" />
          <div>
            <p className="font-medium">Karikues i Ri u Lidh!</p>
            <p className="text-sm text-muted-foreground">{data.charger_id}</p>
            <p className="text-xs text-muted-foreground">{data.vendor} {data.model}</p>
          </div>
        </div>,
        { duration: 10000 }
      );
    } else {
      toast.warning(
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <p className="font-medium">Karikues i Paregjistruar!</p>
            <p className="text-sm text-muted-foreground">{data.charger_id}</p>
            <p className="text-xs text-muted-foreground">{data.vendor} {data.model}</p>
            <p className="text-xs text-amber-600 mt-1">Shko te Karikuesit për ta regjistruar</p>
          </div>
        </div>,
        { duration: 15000 }
      );
    }
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`
            p-4 rounded-lg shadow-lg border backdrop-blur-sm
            animate-in slide-in-from-right duration-300
            ${notification.is_registered 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-amber-500/10 border-amber-500/30'
            }
          `}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              {notification.is_registered ? (
                <CheckCircle className="h-6 w-6 text-green-500 mt-0.5" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-500 mt-0.5" />
              )}
              <div>
                <p className="font-semibold text-foreground">
                  {notification.is_registered ? 'Karikues i Ri u Lidh!' : 'Karikues i Paregjistruar!'}
                </p>
                <div className="mt-1 space-y-0.5">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    {notification.charger_id}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {notification.vendor} {notification.model}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    OCPP {notification.ocpp_version}
                  </p>
                  {notification.serial_number && (
                    <p className="text-xs text-muted-foreground">
                      S/N: {notification.serial_number}
                    </p>
                  )}
                  {notification.site_name && (
                    <p className="text-xs text-muted-foreground">
                      📍 {notification.site_name}
                    </p>
                  )}
                </div>
                {!notification.is_registered && (
                  <p className="text-xs text-amber-500 mt-2">
                    ⚠️ Shko te Karikuesit për ta regjistruar
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => dismissNotification(notification.id)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NewChargerNotification;
